'use client'

import { featureFlagsAtom } from '@polaris/core/stores'
import { useAtom, useAtomValue } from 'jotai'
import Image from 'next/image'
import type { ComponentProps } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { z } from 'zod'
import { Button } from '@/src/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/src/components/ui/dialog'
import { AppleLogo } from '@/src/components/ui/icons/apple-logo'
import { Input } from '@/src/components/ui/input'
import { OTP } from '@/src/components/ui/otp'
import Separator from '@/src/components/ui/separator'
import { errorToast, successToast } from '@/src/components/ui/toast'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import {
  getAppleLoginUrl,
  getAppleMpcLoginUrl,
  isAppleLoginConfigured,
} from '@/src/services/auth/apple'
import { getGoogleLoginUrl } from '@/src/services/auth/google'
import { setWebServerCookieForLocalhost } from '@/src/services/auth/localhost'
import { otpLogin, sendOTPByEmail } from '@/src/services/auth/otp'
import { useAccountsActions } from '@/src/stores/accounts/actions'
import { loginDialogOpenAtom } from '@/src/stores/onboarding'

interface LoginDialogContentProps {
  isVercelPreview?: boolean
  mainCtaCopy?: string | React.ReactNode
  isOpen?: boolean
}

interface LoginDialogProps extends LoginDialogContentProps {
  isOpen: boolean
  onClose: () => void
}

const otpLoginSchema = z.object({
  email: z
    .string()
    .min(1, { message: 'This field has to be filled.' })
    .email('This is not a valid email.'),
})

export function useLoginDialogContent({
  isVercelPreview,
  mainCtaCopy = 'Sign up to save your portfolio',
  isOpen,
}: LoginDialogContentProps) {
  const featureFlags = useAtomValue(featureFlagsAtom)

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  const popupRef = useRef<Window | null>(null)
  const popupTimerRef = useRef<number | null>(null)
  const formRef = useRef<HTMLFormElement | null>(null)

  const { commitAccounts } = useAccountsActions()
  const { ampli } = useAmplitudeAnalytics()

  const resetState = useCallback(() => {
    setOtpSent(false)
    setEmail('')
    setOtpCode('')
    setEmailError('')
    setIsLoading(false)
    setIsResending(false)
    setIsAuthenticating(false)
    if (popupTimerRef.current) {
      window.clearInterval(popupTimerRef.current)
      popupTimerRef.current = null
    }
    try {
      popupRef.current?.close()
    } catch {}
    popupRef.current = null
  }, [])

  useEffect(() => {
    if (isOpen === false) {
      resetState()
    }
  }, [isOpen, resetState])

  const handleEmailInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newEmail = e.target.value
      setEmail(newEmail)
      const result = otpLoginSchema.safeParse({ email: newEmail })
      if (newEmail && !result.success) {
        setEmailError(result.error.errors[0]?.message || 'Invalid email')
      } else {
        setEmailError('')
      }
    },
    [],
  )

  const onClickBack = useCallback(() => {
    setOtpSent(false)
    setEmail('')
    setOtpCode('')
    setEmailError('')
  }, [])

  const submitDisabled = useMemo(() => {
    return !email || !!emailError || (otpSent && otpCode.length < 6)
  }, [email, emailError, otpCode, otpSent])

  const handleSubmitEmail = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      event.stopPropagation()
      if (!otpSent) {
        try {
          otpLoginSchema.parse({ email })
        } catch (err) {
          if (err instanceof z.ZodError) {
            setEmailError(err.errors[0]?.message || 'Invalid email')
          } else {
            setEmailError('Unknown error')
          }
          return
        }
        setIsLoading(true)
        try {
          await sendOTPByEmail({ email })
          setOtpSent(true)
        } catch {
          errorToast('Something went wrong...')
        } finally {
          setIsLoading(false)
        }
      } else {
        try {
          const { access_token } = await otpLogin({ code: otpCode })
          if (process.env.NODE_ENV === 'development') {
            await setWebServerCookieForLocalhost(access_token)
          }
          const success = await commitAccounts
            .mutateAsync({ accessToken: access_token })
            .catch((err) => {
              ampli?.onboardingCreateAccountError({
                errorMsg: err instanceof Error ? err.message : `${err}`,
              })
              errorToast('Failed to save your accounts.')
              event.preventDefault()
              return false
            })
          if (success === false) return
          ampli?.onboardingCreateAccount({ isGuest: false })
          window.location.href = '/'
        } catch (_) {
          errorToast('Invalid code')
        }
      }
    },
    [ampli, commitAccounts, email, otpCode, otpSent],
  )

  const handleResendCode = useCallback(async () => {
    setIsResending(true)
    try {
      await sendOTPByEmail({ email })
      successToast('New verification code sent')
    } catch (_) {
      errorToast('Failed to resend code')
    } finally {
      setIsResending(false)
    }
  }, [email])

  // Handle messages from popup callbacks
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      // Only accept messages from same origin
      if (event.origin !== window.location.origin) return
      if (typeof event.data !== 'object' || !event.data) return
      const { type } = event.data as { type?: string }
      if (type === 'oauth:success' || type === 'oauth:error') {
        setIsAuthenticating(false)
        if (popupTimerRef.current) window.clearInterval(popupTimerRef.current)
        try {
          popupRef.current?.close()
        } catch {}
        // Refresh to pick up auth cookie set by callback page
        if (type === 'oauth:success') {
          window.location.reload()
        }
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  const openOAuthPopup = useCallback((url: string, name: string) => {
    const popup = window.open(url, name, 'width=600,height=700')
    setIsAuthenticating(true)
    popupRef.current = popup
    if (popup) {
      popup.focus()
      popupTimerRef.current = window.setInterval(() => {
        if (popup.closed) {
          setIsAuthenticating(false)
          if (popupTimerRef.current) window.clearInterval(popupTimerRef.current)
        }
      }, 150)
    }
  }, [])

  console.log('featureFlags.otpLoginOnWeb', featureFlags.otpLoginOnWeb)

  const dialogContentProps: Partial<ComponentProps<typeof DialogContent>> = {
    'aria-describedby': 'login dialog',
    className: 'max-w-md min-h-[unset] h-fit pt-0 overflow-hidden',
    showBackButton: otpSent,
    showCloseButton: true,
    closeButtonProps: otpSent
      ? undefined
      : {
          variant: 'secondary' as const,
          className: '!px-3',
        },
    backAction: onClickBack,
    headerClassName: !otpSent
      ? 'absolute top-0 left-0 right-0 dark'
      : undefined,
  }

  const content = (
    <div className='p-8 h-full overflow-y-auto rounded-b-[32px]'>
      {!otpSent && (
        <div className='relative -m-8 mb-8 max-h-52 flex items-center overflow-hidden'>
          <Image
            src='https://s3.polaris.app/artworks/girl-backpack-field-house.png'
            alt='Login artwork'
            width={448}
            height={252}
            unoptimized
          />
          <div className='pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-foreground' />
        </div>
      )}

      <DialogHeader className='mb-8 flex-col items-center gap-2'>
        <DialogTitle className='font-serif font-extralight'>
          {otpSent ? 'Enter 6-digit code' : mainCtaCopy}
        </DialogTitle>
        {!otpSent && (
          <DialogDescription>
            {featureFlags.privacyUi
              ? 'Create an account to privately trade all of your favorite assets, in one unified portfolio.'
              : 'Track all of your wallets from anywhere in one unified portfolio.'}
          </DialogDescription>
        )}
        {otpSent && (
          <DialogDescription>
            One-time temporary login code sent to {email}.{' '}
            <button
              type='button'
              className='underline inline hover:text-link-hover transition-colors focus-outer rounded'
              onClick={onClickBack}
            >
              Not you?
            </button>
          </DialogDescription>
        )}
      </DialogHeader>

      {!otpSent && !isVercelPreview && (
        <div className='flex flex-col gap-2'>
          <Button
            variant='outline'
            className='w-full'
            type='button'
            disabled={isAuthenticating || !!isVercelPreview}
            onClick={
              !isVercelPreview
                ? (e) => {
                    e.preventDefault()
                    openOAuthPopup(getGoogleLoginUrl(), 'Google Login')
                  }
                : undefined
            }
          >
            <Image
              src={'/assets/oauth/google.svg'}
              alt='Google logo'
              width={24}
              height={24}
            />{' '}
            Continue with Google
          </Button>
          {!isVercelPreview &&
            featureFlags.signInWithAppleOnWeb &&
            isAppleLoginConfigured && (
              <Button
                variant='outline'
                className='w-full'
                type='button'
                disabled={isAuthenticating}
                onClick={(e) => {
                  e.preventDefault()
                  // Use MPC URL when newMpcAuthFlow is enabled (returns code to frontend)
                  // Otherwise use legacy URL (POSTs to backend)
                  const appleUrl = featureFlags.newMpcAuthFlow
                    ? getAppleMpcLoginUrl()
                    : getAppleLoginUrl()
                  openOAuthPopup(appleUrl, 'Apple Login')
                }}
              >
                <AppleLogo className='w-6 h-6' />
                Continue with Apple
              </Button>
            )}
          {featureFlags.privacyUi && (
            <Button variant='outline' className='w-full' type='button' disabled>
              Continue with a wallet{' '}
              <span className='text-tertiary text-xs'>Coming soon</span>
            </Button>
          )}
          {isVercelPreview && (
            <p className='text-secondary text-xs mt-2'>
              Social authentication is unavailable in Vercel previews. Please
              use email authentication. (OAuth providers do not support wildcard
              domains, meaning the URL must be consistent which is not possible
              with Vercel previews.)
            </p>
          )}
        </div>
      )}

      {!otpSent && featureFlags.otpLoginOnWeb && (
        <Separator className='my-4'>
          <p className='text-secondary'>OR</p>
        </Separator>
      )}

      <form
        ref={formRef}
        className='flex flex-col gap-4'
        onSubmit={handleSubmitEmail}
      >
        {!otpSent && featureFlags.otpLoginOnWeb ? (
          <Input
            name='email'
            type='email'
            dimension='medium'
            placeholder='Enter your email'
            value={email}
            onChange={handleEmailInput}
            isInvalid={!!emailError}
            disabled={isAuthenticating}
            onNextCta={
              !submitDisabled &&
              !isLoading &&
              otpLoginSchema.safeParse({ email }).success
                ? () => formRef.current?.requestSubmit()
                : undefined
            }
          />
        ) : !otpSent ? null : (
          <>
            <OTP
              name='code'
              value={otpCode}
              onChange={setOtpCode}
              containerClassName='mt-2'
              slotClassName='bg-foreground'
              autoFocus
            />
            <div className='flex items-center justify-center gap-2 text-center mt-2 mb-6'>
              <p className='text-secondary text-sm'>
                Didn&apos;t receive a code?
              </p>
              <Button
                variant='ghost'
                size='sm'
                onClick={handleResendCode}
                disabled={isResending}
                type='button'
              >
                {isResending ? 'Sending...' : 'Resend'}
              </Button>
            </div>
          </>
        )}
        {otpSent && (
          <Button
            type='submit'
            variant={submitDisabled ? 'secondary' : 'primary'}
            className='w-full mt-16'
            disabled={submitDisabled || isLoading}
          >
            Continue
          </Button>
        )}
      </form>
    </div>
  )

  return { dialogContentProps, content, isInitialState: !otpSent }
}

function LoginDialog({
  isOpen,
  onClose,
  isVercelPreview,
  mainCtaCopy,
}: LoginDialogProps) {
  const { dialogContentProps, content } = useLoginDialogContent({
    isVercelPreview,
    mainCtaCopy,
    isOpen,
  })

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent {...dialogContentProps}>{content}</DialogContent>
    </Dialog>
  )
}

interface LoginDialogRootProps {
  isVercelPreview?: boolean
  mainCtaCopy?: string
}

export function LoginDialogRoot({
  isVercelPreview,
  mainCtaCopy,
}: LoginDialogRootProps) {
  const [isOpen, setIsOpen] = useAtom(loginDialogOpenAtom)

  return (
    <LoginDialog
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      isVercelPreview={isVercelPreview}
      mainCtaCopy={mainCtaCopy}
    />
  )
}
